import { useEffect, useRef, useCallback } from 'react';
import type { WSMessage, PriceUpdatePayload, CandleUpdatePayload, SignalAlertPayload } from '@arc/shared';
import { useTerminalStore } from '../stores/terminal.store.js';
import { noteServerPrices } from './usePriceData.js';

// Same-origin by default: nginx (prod) and the Vite dev proxy both forward /ws
// to the API, so the bundle never needs a host baked in. VITE_WS_URL overrides.
function resolveWSUrl(): string {
  const override = import.meta.env.VITE_WS_URL;
  if (override) return override;
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}/ws`;
}

const WS_URL = resolveWSUrl();
const RECONNECT_DELAY_MS = 3000;
const PING_INTERVAL_MS   = 25000;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWebSocket() {
  const wsRef        = useRef<WebSocket | null>(null);
  const pingRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef   = useRef(true);

  const { updatePrices, pushCandle, pushSignal, setWSConnected, activePanels } =
    useTerminalStore();

  const clearTimers = useCallback(() => {
    if (pingRef.current)      clearInterval(pingRef.current);
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
  }, []);

  const subscribe = useCallback((ws: WebSocket) => {
    const symbols    = [...new Set(activePanels.map(p => p.symbol))];
    const timeframes = [...new Set(activePanels.map(p => p.timeframe))];
    ws.send(JSON.stringify({
      type:      'subscribe',
      payload:   { symbols, timeframes },
      timestamp: Date.now(),
    } satisfies WSMessage<{ symbols: string[]; timeframes: string[] }>));
  }, [activePanels]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    clearTimers();

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) { ws.close(); return; }
      setWSConnected(true);
      subscribe(ws);
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping', payload: null, timestamp: Date.now() }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(event.data) as WSMessage;
        switch (msg.type) {
          case 'price_update': {
            const p = msg.payload as PriceUpdatePayload;
            updatePrices(p.updates);
            noteServerPrices(p.updates.map(u => u.symbol));
            break;
          }
          case 'candle_update': {
            const p = msg.payload as CandleUpdatePayload;
            pushCandle(p.symbol, p.timeframe, p.candle);
            break;
          }
          case 'signal_alert': {
            const p = msg.payload as SignalAlertPayload;
            pushSignal(p.signal);
            break;
          }
          case 'pong':
            break;
        }
      } catch {
        // Silently ignore malformed messages
      }
    };

    ws.onerror = () => {
      // onclose fires next and handles reconnect
    };

    ws.onclose = () => {
      clearTimers();
      setWSConnected(false);
      if (mountedRef.current) {
        reconnectRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };
  }, [subscribe, setWSConnected, updatePrices, pushCandle, pushSignal, clearTimers]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimers();
      wsRef.current?.close();
    };
  }, [connect, clearTimers]);

  const sendMessage = useCallback(<T>(msg: WSMessage<T>) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  return { sendMessage };
}
